import React, { useEffect } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchKPI, fetchEmployee, fetchCandidate } from "../../../redux/slice/dashboardChartSlice";
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import "./AdminChart.scss";
ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

const AdminChart = () => {
    const formatLabel = (label) => {
        return label
            ?.toLowerCase()
            .split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const dispatch = useDispatch();
    const dashboardState = useSelector((state) => state.dashboard);
    const { kpi, employee, candidate, loading, error } = dashboardState || {};

    useEffect(() => {
        dispatch(fetchKPI());
        dispatch(fetchEmployee());
        dispatch(fetchCandidate());
    }, [dispatch]);

    if (!dashboardState) {
        return <div className="text-center mt-5">Dashboard not initialized</div>;
    }
    if (loading || !kpi || !employee || !candidate) {
        return <div className="text-center mt-5">Loading...</div>;
    }
    if (error) {
        return <div className="text-danger text-center">{error}</div>;
    }
    return (
        <div className="admin-chart-container">
            {/* KPI =========== */}
            <Row className="chart-row">
                <Col md={3}>
                    <Card className="kpi-card kpi-purple">
                        <Card.Body>
                            <div className="kpi-header">Total Employees</div>
                            <div className="kpi-content">
                                <h4>{kpi.totalEmployees}</h4>
                                <i className="fa fa-users kpi-icon"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card className="kpi-card kpi-purple2">
                        <Card.Body>
                            <div className="kpi-header">Candidates in Pipeline</div>
                            <div className="kpi-content">
                                <h4>{kpi.candidates}</h4>
                                <i className="fa fa-briefcase kpi-icon"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card className="kpi-card kpi-orange">
                        <Card.Body>
                            <div className="kpi-header">Pending Approvals</div>
                            <div className="kpi-content">
                                <h4>{kpi.pending}</h4>
                                <i className="fa fa-clock kpi-icon"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* EMPLOYEE + CANDIDATE ===============*/}
            <Row className="chart-row">
                {/* Employee Status */}
                <Col md={4}>
                    <Card className="chart-card">
                        <Card.Body>
                            <h5>Employee Status</h5>
                            <div className="employee-status-wrapper">
                                <div className="employee-chart">
                                    {employee.status.data.every(item => item === 0) ? (
                                            <div className="empty-chart">
                                                <div className="empty-chart-center">
                                                    No Data
                                                </div>
                                            </div>
                                        ) : (
                                            <Pie
                                                data={{
                                                    labels: employee.status.labels.map(formatLabel),
                                                    datasets: [
                                                        {
                                                            data: employee.status.data,
                                                            backgroundColor: [
                                                                "#5b2dbf",
                                                                "#c06bbf",
                                                                "#b39ddb",
                                                                "#f2c078",
                                                                "#c5c6d0"
                                                            ],
                                                            borderWidth: 2,
                                                            borderColor: "#fff"
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    plugins: {
                                                        legend: {
                                                            display: false
                                                        }
                                                    }
                                                }}
                                            />
                                        )
                                    }
                                </div>
                                <div className="employee-legend">
                                    {!employee.status.data.every(item => item === 0) &&
                                        employee.status.labels.map((label, index) => {
                                            const colors = [
                                                "#5b2dbf",
                                                "#c06bbf",
                                                "#b39ddb",
                                                "#f2c078",
                                                "#c5c6d0"
                                            ];
                                            return (
                                                <div key={index} className="legend-item">
                                                    <span
                                                        className="legend-dot"
                                                        style={{ backgroundColor: colors[index] }}
                                                    ></span>
                                                    <span> {label} ({employee.status.data[index]})</span>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Department */}
                <Col md={4}>
                    <Card className="chart-card">
                        <Card.Body>
                            <h5>Department Wise</h5>
                            <div className="employee-status-wrapper">
                                {employee.department.data.every(item => item === 0) ? (
                                        <div className="empty-chart">
                                            <div className="empty-chart-center">
                                                No Data
                                            </div>
                                        </div>
                                    ) : (
                                        <Bar
                                            data={{
                                                labels: employee.department.labels.map(formatLabel),
                                                datasets: [
                                                    {
                                                        label: "Departments", // <-- undefined fix
                                                        data: employee.department.data,
                                                        backgroundColor:
                                                            employee.department.labels.map((_, index) => {
                                                                const colors = [
                                                                    "#793282",
                                                                    "#512F9F",
                                                                    "#ff4d4f",
                                                                    "#9C27B0",
                                                                    "#673AB7",
                                                                    "#E91E63"
                                                                ];
                                                                return colors[index % colors.length];
                                                            }),
                                                        borderRadius: 6
                                                    }
                                                ]
                                            }}
                                            options={{
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    }
                                                }
                                            }}
                                        />
                                    )
                                }
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                {/* Candidate Status ===============*/}
                <Col md={4}>
                    <Card className="chart-card">
                        <Card.Body>
                            <h5>Candidate Status</h5>
                            <div className="employee-status-wrapper">
                                <div className="employee-chart">
                                    {candidate.status.data.every((item) => item === 0) ? (
                                        <div className="empty-chart">
                                            <div className="empty-chart-center">
                                                No Data
                                            </div>
                                        </div>
                                    ) : (
                                        <Pie
                                            data={{
                                                labels: candidate.status.labels,
                                                datasets: [
                                                    {
                                                        data: candidate.status.data,
                                                        backgroundColor: [
                                                            "#793282",
                                                            "#512F9F",
                                                            "#ff4d4f"
                                                        ],
                                                        borderWidth: 2,
                                                        borderColor: "#fff"
                                                    }
                                                ]
                                            }}
                                            options={{
                                                plugins: {
                                                    legend: { display: false }
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                                <div className="employee-legend">
                                    {candidate.status.labels.map((label, index) => {
                                        const colors = [
                                            "#793282",
                                            "#512F9F",
                                            "#ff4d4f"
                                        ];
                                        return (
                                            <div key={index} className="legend-item">
                                                <span
                                                    className="legend-dot"
                                                    style={{
                                                        backgroundColor: colors[index]
                                                    }}
                                                ></span>
                                                <span>
                                                    {label} (
                                                    {candidate.status.data[index]})
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* FUNNEL ======================*/}
            <Row className="chart-row">
                <Col md={12}>
                    <Card className="chart-card">
                        <Card.Body>
                            <h5>Hiring Funnel</h5>
                            <div className="funnel-horizontal">
                                {candidate.funnel.map((item, index) => (
                                    <button key={index} className={`funnel-box step-${index}`}>
                                        <div className="funnel-text">
                                            <span>{item.label}</span>
                                            <b>{item.value}</b>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
export default AdminChart;
